import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import TinyColor from 'tinycolor2';

import { TextButton, Button } from 'components/core';
import useMediaQuery, { toMatchMediaQuery } from 'shared/hooks/use-media-query';

/** Middle dot (·) between order date/time and dispensary address (matches order history UI). */
const DATE_ADDRESS_SEPARATOR = ' \u00b7 ';

export type OrderRowProps = {
  title: string;
  date: string;
  address?: string;
  desktopCol2: React.ReactNode;
  desktopCol3?: React.ReactNode;
  mobileCol2?: React.ReactNode;
  mobileCol3?: React.ReactNode;
  actions: React.ReactNode;
  thumbnails?: React.ReactNode;
};

export function OrderRow({
  title,
  date,
  address,
  desktopCol2,
  desktopCol3,
  mobileCol2,
  mobileCol3,
  actions,
  thumbnails,
}: OrderRowProps): JSX.Element {
  const theme = useTheme();
  const mdQuery = useMemo(() => toMatchMediaQuery(theme.breakpoints.up('md')), [theme]);
  const isMdUp = useMediaQuery(mdQuery);

  const metaLine =
    address !== undefined && address !== '' ? (
      <>
        {date}
        {DATE_ADDRESS_SEPARATOR}
        {address}
      </>
    ) : (
      date
    );

  /** On mobile, actions follow product thumbnails; on desktop, actions stay in row 1. */
  return (
    <StyledOrderRow>
      <MobileDetails data-testid='order-row-details'>
        <MobilePrimaryBand>
          <TypeDateCell>
            <OrderTitle>{title}</OrderTitle>
          </TypeDateCell>
          {(mobileCol2 ?? mobileCol3) && (
            <MobileMetaCell>
              {mobileCol2 && <span>{mobileCol2}</span>}
              {mobileCol3 && <span>{mobileCol3}</span>}
            </MobileMetaCell>
          )}
        </MobilePrimaryBand>
        <MobileMetaLine>
          <OrderMeta>{metaLine}</OrderMeta>
        </MobileMetaLine>
      </MobileDetails>

      <DesktopTypeDate>
        <OrderTitle>{title}</OrderTitle>
      </DesktopTypeDate>
      <DesktopInfoCell>{desktopCol2}</DesktopInfoCell>
      <DesktopInfoCell>{desktopCol3}</DesktopInfoCell>

      {/* One actions mount: desktop grid cell vs mobile stack, keyed off the same md breakpoint as CSS. */}
      {isMdUp && <ActionsCell data-testid='order-row-actions-desktop'>{actions}</ActionsCell>}

      <DesktopMetaLine>
        <OrderMeta>{metaLine}</OrderMeta>
      </DesktopMetaLine>

      {thumbnails && <ThumbnailsRow>{thumbnails}</ThumbnailsRow>}

      {!isMdUp && <MobileStackedActions data-testid='order-row-actions-mobile'>{actions}</MobileStackedActions>}
    </StyledOrderRow>
  );
}

const StyledOrderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.5fr);
    align-items: center;
    gap: 0;
  }
`;

const MobileDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: none;
  }
`;

const MobilePrimaryBand = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: flex-start;
  width: 100%;
`;

const TypeDateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 0 0;
  min-width: 120px;
`;

const MobileMetaCell = styled.div`
  display: flex;
  gap: 32px;
  justify-content: flex-end;
  flex: 1 0 0;
  min-width: 120px;
  font-size: 13px;
  line-height: 1.65;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  align-items: flex-start;
`;

const MobileMetaLine = styled.div`
  width: 100%;
  min-width: 0;
`;

/** Actions below thumbnails on narrow viewports; desktop uses ActionsCell in the grid instead. */
const MobileStackedActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const DesktopTypeDate = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

const DesktopInfoCell = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-size: 13px;
    line-height: 1.65;
    color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
    padding-left: 8px;
  }
`;

const DesktopMetaLine = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: block;
    grid-column: 1 / -1;
    width: 100%;
    min-width: 0;
  }
`;

const ActionsCell = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    justify-content: flex-start;
    gap: 16px;
    padding-left: 8px;
  }
`;

const OrderTitle = styled.strong`
  font-size: 16px;
  font-weight: bold;
  line-height: 1.2;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  display: block;
  margin: 0;
`;

const OrderMeta = styled.p`
  font-size: 13px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.grey[30]};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OrderRowCellLabel = styled.label`
  font-size: 11px;
  font-weight: bold;
  line-height: 150%;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[60]};
`;

export const OrderRowTextButton = styled(TextButton)`
  font-size: 13px;
  color: var(--dct--customized-colors-buttonsLinks);
  padding: 0;
  white-space: nowrap;
`;

const ThumbnailsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up('md')} {
    grid-column: 1 / -1;
    order: 1;
  }
`;

export const OrderRowButton = styled(Button)`
  background-color: ${({ theme }) => TinyColor.mix(theme.customized.colors.buttonsLinks, '#fff', 85).toRgbString()};
  border: none;
  border-radius: 9999px;
  color: var(--dct--customized-colors-buttonsLinks);
  font-size: 12px;
  font-weight: bold;
  padding: 8px 20px;
  text-transform: uppercase;
  white-space: nowrap;
  width: 160px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: auto;
  }
`;
